/**
 * Période d’affichage d’un bloc (`startAt` / `endAt` sur `widget_workspace`).
 * API : ISO ; formulaire : `Date` à minuit ; fin de journée incluse pour `endAt`.
 */
import { endOfDay, formatISO, isValid, parseISO, startOfDay } from 'date-fns';

/** Dates choisies dans l’éditeur (null = pas de limite). */
export type WorkspaceScheduleState = {
  startAt: Date | null;
  endAt: Date | null;
};

/** État initial création de bloc (affichage permanent tant qu’aucune date n’est saisie). */
export const emptyWorkspaceSchedule = (): WorkspaceScheduleState => ({
  startAt: null,
  endAt: null
});

/**
 * Parse une date API en jour calendaire local (minuit).
 * Utilise la partie `YYYY-MM-DD` pour éviter les décalages UTC (CI vs poste dev).
 */
export const parseApiScheduleDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const trimmed = value.trim();
  const datePart = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (datePart) {
    const year = Number(datePart[1]);
    const monthIndex = Number(datePart[2]) - 1;
    const day = Number(datePart[3]);
    const calendarDate = new Date(year, monthIndex, day);
    return isValid(calendarDate) ? startOfDay(calendarDate) : null;
  }
  const parsed = parseISO(trimmed);
  return isValid(parsed) ? startOfDay(parsed) : null;
};

/** Lit `startAt` / `endAt` d’un workspace pour préremplir le formulaire. */
export const scheduleFromWorkspace = (workspace?: {
  startAt?: string | null;
  endAt?: string | null;
}): WorkspaceScheduleState => ({
  startAt: parseApiScheduleDate(workspace?.startAt),
  endAt: parseApiScheduleDate(workspace?.endAt)
});

export const toApiScheduleStartAt = (date: Date | null): string | null =>
  date ? formatISO(startOfDay(date)) : null;

export const toApiScheduleEndAt = (date: Date | null): string | null =>
  date ? formatISO(endOfDay(date)) : null;

/** Payload PATCH/POST workspace (dates normalisées pour l’API). */
export const toApiSchedulePayload = (schedule: WorkspaceScheduleState) => ({
  startAt: toApiScheduleStartAt(schedule.startAt),
  endAt: toApiScheduleEndAt(schedule.endAt)
});

/** Snapshot JSON pour détecter des changements non enregistrés sur la planification. */
export const createSchedulingSnapshot = (schedule: WorkspaceScheduleState): string =>
  JSON.stringify({
    startAt: schedule.startAt?.toISOString() ?? null,
    endAt: schedule.endAt?.toISOString() ?? null
  });

/** `startAt` doit être strictement avant `endAt` lorsque les deux sont renseignés. */
export const isScheduleRangeValid = (schedule: WorkspaceScheduleState): boolean => {
  if (!schedule.startAt || !schedule.endAt) return true;
  return startOfDay(schedule.startAt).getTime() < startOfDay(schedule.endAt).getTime();
};
